import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { LogOut, User, Home, Dog, Menu, X, Bell, Calendar, CreditCard, BarChart3, BookOpen } from 'lucide-react';
import Portal from '../Portal';

const Header = () => {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    setShowNotifications(false);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    const baseOffset = 80; // altura aproximada do header + respiro

    const updateEditButtonOffset = () => {
      if (isMobileMenuOpen && mobileMenuRef.current) {
        const menuHeight = mobileMenuRef.current.getBoundingClientRect().height;
        root.style.setProperty('--home-edit-top', `${baseOffset + menuHeight}px`);
        return;
      }
      root.style.setProperty('--home-edit-top', `${baseOffset}px`);
    };

    updateEditButtonOffset();
    window.addEventListener('resize', updateEditButtonOffset);

    return () => {
      window.removeEventListener('resize', updateEditButtonOffset);
      root.style.setProperty('--home-edit-top', `${baseOffset}px`);
    };
  }, [isMobileMenuOpen]);

  // Função para formatar nome com apenas a primeira letra maiúscula
  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  // Função para obter o primeiro nome do usuário
  const getUserDisplayName = () => {
    const fullName = user.nomeExibicao || user.nome || user.displayName || '';
    if (fullName) {
      // Extrair apenas o primeiro nome (primeira palavra)
      const firstName = fullName.trim().split(/\s+/)[0];
      return capitalizeFirst(firstName);
    }
    // Se não tiver nome, usar primeira parte do email (antes do @)
    if (user.email) {
      const emailName = user.email.split('@')[0];
      return capitalizeFirst(emailName);
    }
    return 'Usuário';
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Erro ao fazer logout
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };


  return (
    <header className="bg-card-light dark:bg-card-dark shadow-sm border-b border-border-light dark:border-border-dark sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex justify-between items-center h-16">
          {/* Logo e Nome do Sistema */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center sm:flex-col sm:items-start leading-tight">
              <div className="flex items-center space-x-2">
                <img 
                  src="/Preto.png" 
                  alt="Aruk Dog Logo" 
                  className="w-12 h-12"
                />
                <span className="hidden sm:inline text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <span className="text-primary-light dark:text-primary-dark">Aruk</span>
                  {' '}
                  <span className="text-black dark:text-black">Dog</span>
                </span>
              </div>
              <span className="hidden sm:block h-px bg-border-light dark:bg-border-dark mt-0" style={{ width: 'calc(100% + 0.1rem)' }}></span>
            </Link>
          </div>

          {/* Navegação Mobile (3 primeiras páginas) */}
          <nav className="md:hidden flex flex-1 items-center px-2 gap-1">
            <Link
              to="/"
              aria-label="Início"
              title="Início"
              className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${location.pathname === '/' ? 'bg-primary-50 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:bg-primary-50 dark:hover:bg-primary-dark/10'}`}
            >
              <Home className="w-5 h-5" />
            </Link>
            <Link
              to="/agenda"
              aria-label="Agenda"
              title="Agenda"
              className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${location.pathname === '/agenda' ? 'bg-primary-50 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:bg-primary-50 dark:hover:bg-primary-dark/10'}`}
            >
              <Calendar className="w-5 h-5" />
            </Link>
            <Link
              to="/pets"
              aria-label="Pets"
              title="Pets"
              className={`flex-1 flex items-center justify-center p-2 rounded-md transition-colors ${location.pathname === '/pets' ? 'bg-primary-50 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:bg-primary-50 dark:hover:bg-primary-dark/10'}`}
            >
              <Dog className="w-5 h-5" />
            </Link>
          </nav>

          {/* Navegação Principal (itens da antiga Sidebar) */}
          <nav className="hidden md:flex items-center space-x-3">
            <div className={`bg-card-light dark:bg-card-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark px-3 py-1 ${location.pathname === '/' ? 'bg-primary-50 dark:bg-primary-dark/20 border-primary-200 dark:border-primary-dark' : ''}`}>
              <Link 
                to="/" 
                className={`text-sm font-medium transition-colors flex items-center space-x-1 ${location.pathname === '/' ? 'text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:text-primary-light dark:hover:text-primary-dark'}`}
              >
                <Home className="w-4 h-4" />
                <span>Início</span>
              </Link>
            </div>
            <div className={`bg-card-light dark:bg-card-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark px-3 py-1 ${location.pathname === '/agenda' ? 'bg-primary-50 dark:bg-primary-dark/20 border-primary-200 dark:border-primary-dark' : ''}`}>
              <Link 
                to="/agenda" 
                className={`text-sm font-medium transition-colors flex items-center space-x-2 ${location.pathname === '/agenda' ? 'text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:text-primary-light dark:hover:text-primary-dark'}`}
              >
                <Calendar className="w-4 h-4" />
                <span>Agenda</span>
              </Link>
            </div>
            <div className={`bg-card-light dark:bg-card-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark px-3 py-1 ${location.pathname === '/pets' ? 'bg-primary-50 dark:bg-primary-dark/20 border-primary-200 dark:border-primary-dark' : ''}`}>
              <Link 
                to="/pets" 
                className={`text-sm font-medium transition-colors flex items-center space-x-2 ${location.pathname === '/pets' ? 'text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:text-primary-light dark:hover:text-primary-dark'}`}
              >
                <Dog className="w-4 h-4" />
                <span>Pets</span>
              </Link>
            </div>
            <div className={`hidden md:block bg-card-light dark:bg-card-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark px-3 py-1 ${location.pathname === '/transactions' ? 'bg-primary-50 dark:bg-primary-dark/20 border-primary-200 dark:border-primary-dark' : ''}`}>
              <Link 
                to="/transactions" 
                className={`text-sm font-medium transition-colors flex items-center space-x-2 ${location.pathname === '/transactions' ? 'text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:text-primary-light dark:hover:text-primary-dark'}`}
              >
                <CreditCard className="w-4 h-4" />
                <span>Transações</span>
              </Link>
            </div>
            <div className={`hidden md:block bg-card-light dark:bg-card-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark px-3 py-1 ${location.pathname === '/dashboard' ? 'bg-primary-50 dark:bg-primary-dark/20 border-primary-200 dark:border-primary-dark' : ''}`}>
              <Link 
                to="/dashboard" 
                className={`text-sm font-medium transition-colors flex items-center space-x-2 ${location.pathname === '/dashboard' ? 'text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:text-primary-light dark:hover:text-primary-dark'}`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Dashboard</span>
              </Link>
            </div>
            <div className={`bg-card-light dark:bg-card-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark px-3 py-1 ${location.pathname === '/blog' || location.pathname.startsWith('/blog/') ? 'bg-primary-50 dark:bg-primary-dark/20 border-primary-200 dark:border-primary-dark' : ''}`}>
              <Link 
                to="/blog" 
                className={`text-sm font-medium transition-colors flex items-center space-x-2 ${location.pathname === '/blog' || location.pathname.startsWith('/blog/') ? 'text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:text-primary-light dark:hover:text-primary-dark'}`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Blog</span>
              </Link>
            </div>
          </nav>


          {/* Menu do Usuário */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {user && (
              <>
                {/* Ícone de Notificações */}
                <div className="relative">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="p-2 text-muted-light dark:text-muted-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors relative"
                    title="Notificações"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  
                  {/* Dropdown de Notificações */}
                  {showNotifications && (
                    <Portal>
                      <div 
                        className="fixed inset-0 z-[10000]" 
                        onClick={() => setShowNotifications(false)}
                      >
                        <div 
                          className="absolute left-0 right-0 top-16 z-[10001] w-full max-h-96 overflow-hidden rounded-none border border-border-light bg-card-light shadow-lg dark:border-border-dark dark:bg-card-dark sm:left-auto sm:right-4 sm:w-80 sm:rounded-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="p-4 border-b border-border-light dark:border-border-dark flex items-center justify-between">
                            <h3 className="font-semibold text-foreground-light dark:text-foreground-dark">
                              Notificações {unreadCount > 0 && `(${unreadCount})`}
                            </h3>
                            <div className="flex items-center gap-2">
                              {unreadCount > 0 && (
                                <button
                                  onClick={() => {
                                    markAllAsRead();
                                    setShowNotifications(false);
                                  }}
                                  className="text-sm text-primary-light dark:text-primary-dark hover:underline"
                                >
                                  Marcar todas como lidas
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="overflow-y-auto max-h-80">
                            {notifications.length === 0 ? (
                              <div className="p-4 text-center text-muted-light dark:text-muted-dark">
                                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p>Nenhuma notificação</p>
                              </div>
                            ) : (
                              <div className="divide-y divide-border-light dark:divide-border-dark">
                                {notifications.map((notification) => (
                                  <div
                                    key={notification.id}
                                    onClick={() => {
                                      if (!notification.lida) {
                                        markAsRead(notification.id);
                                      }
                                      setShowNotifications(false);
                                    }}
                                    className={`p-4 hover:bg-primary-50 dark:hover:bg-primary-dark/10 transition-colors cursor-pointer ${
                                      !notification.lida ? 'bg-primary-50 dark:bg-primary-dark/10' : ''
                                    }`}
                                  >
                                    <div className="flex items-start space-x-3">
                                      <div className={`w-2 h-2 rounded-full mt-1.5 ${
                                        !notification.lida ? 'bg-primary-light dark:text-primary-dark' : 'bg-transparent'
                                      }`}></div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground-light dark:text-foreground-dark">
                                          {notification.titulo}
                                        </p>
                                        <p className="text-sm text-muted-light dark:text-muted-dark mt-1">
                                          {notification.mensagem}
                                        </p>
                                        <p className="text-xs text-muted-light dark:text-muted-dark mt-2">
                                          {notification.criadoEm?.toDate ? 
                                            notification.criadoEm.toDate().toLocaleString('pt-BR', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            }) : 
                                            new Date(notification.criadoEm).toLocaleString('pt-BR', {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })
                                          }
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Portal>
                  )}
                </div>
                
                {/* Informação do Usuário Logado */}
                <Link
                  to="/users"
                  className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-secondary-light dark:bg-secondary-dark rounded-lg border border-border-light dark:border-border-dark hover:bg-primary-50 dark:hover:bg-primary-dark/10 transition-colors"
                  title="Ver usuários"
                >
                  <User className="w-4 h-4 text-muted-light dark:text-muted-dark" />
                  <span className="text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    {getUserDisplayName()}
                  </span>
                </Link>
                
                <button
                  onClick={handleLogout}
                  className="hidden sm:block p-2 text-muted-light dark:text-muted-dark hover:text-destructive-light dark:hover:text-destructive-dark transition-colors"
                  title="Sair"
                >
                  <LogOut className="w-5 h-5" />
                </button>
                
                {/* Botão Menu Mobile */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden p-2 text-muted-light dark:text-muted-dark hover:text-primary-light dark:hover:text-primary-dark transition-colors"
                  title="Menu"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Menu Mobile */}
      {user && isMobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden bg-card-light dark:bg-card-dark border-t border-border-light dark:border-border-dark"
        >
          <nav className="px-4 py-3 space-y-2">
            <Link 
              to="/blog" 
              onClick={closeMobileMenu}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === '/blog' || location.pathname.startsWith('/blog/') ? 'bg-primary-50 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark' : 'text-foreground-light dark:text-foreground-dark hover:bg-primary-50 dark:hover:bg-primary-dark/10'}`}
            >
              <BookOpen className="w-5 h-5" />
              <span className="font-medium">Blog</span>
            </Link>
            
            <div className="border-t border-border-light dark:border-border-dark pt-2 mt-2 space-y-2">
              {/* Perfil / conta — link (no desktop o atalho fica na barra superior) */}
              <Link
                to="/users"
                onClick={closeMobileMenu}
                className="mx-2 flex items-center space-x-3 rounded-lg bg-secondary-light px-4 py-3 transition-colors hover:bg-primary-50 active:bg-primary-100 dark:bg-secondary-dark dark:hover:bg-primary-dark/20 dark:active:bg-primary-dark/30"
                title="Minha conta e perfil"
              >
                <User className="h-5 w-5 shrink-0 text-muted-light dark:text-muted-dark" />
                <div className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-sm font-medium text-foreground-light dark:text-foreground-dark">
                    {getUserDisplayName()}
                  </span>
                  <span className="block truncate text-xs text-muted-light dark:text-muted-dark">
                    Toque para ver usuários
                  </span>
                </div>
              </Link>
              
              <button
                onClick={() => {
                  closeMobileMenu();
                  handleLogout();
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-destructive-light dark:text-destructive-dark hover:bg-destructive-light/10 dark:hover:bg-destructive-dark/10 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sair</span>
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
